import { Document, Packer, Paragraph, TextRun } from "docx";
import { saveAs } from "file-saver";

function formatDate(dateStr) {
  if (!dateStr) return "Date inconnue";
  const d = new Date(dateStr);

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function htmlToCleanText(html = "") {
  if (!html) return "";

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const insertBlockText = (el, text) => {
    if (!el || !el.parentNode) return;
    el.replaceWith(doc.createTextNode(`\n${text}\n`));
  };

  const removeNode = (el) => {
    if (el?.parentNode) el.remove();
  };

  // Petits helpers
  const safeReplaceText = (value = "") =>
    value
      .replace(/Options d['’]affichage/gi, "")
      .replace(/Options/gi, "");

  // 1) Retirer les éléments d'UI d'édition / affichage qui ne doivent jamais sortir
  doc.querySelectorAll(
    [
      ".ql-facebook-embed-hint",
      ".ql-facebook-embed-hit",
      ".ql-iframe-resizer",
      ".ql-facebook-embed-controls",
      ".ql-embed-hint",
      ".ql-embed-controls",
      ".ql-embed-toolbar",
      ".ql-resizer",
      "script",
      "style",
      "svg",
      "canvas",
      "source",
      "embed",
      "object",
    ].join(", ")
  ).forEach(removeNode);

  // 2) Nettoyage défensif du texte brut dans tous les noeuds texte
  const textNodes = [];
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  let currentNode = walker.nextNode();
  while (currentNode) {
    textNodes.push(currentNode);
    currentNode = walker.nextNode();
  }

  textNodes.forEach((node) => {
    node.textContent = safeReplaceText(node.textContent || "");
  });

  // 3) Remplacer les embeds réseaux sociaux / iframe par le libellé métier attendu
  doc.querySelectorAll(".ql-instagram").forEach((el) => {
    insertBlockText(el, "Post : Instagram");
  });

  doc.querySelectorAll(".ql-tweet, blockquote.twitter-tweet, .twitter-tweet").forEach((el) => {
    insertBlockText(el, "Post : X");
  });

  doc.querySelectorAll(".ql-facebook-embed").forEach((el) => {
    insertBlockText(el, "Post : Facebook");
  });

  // 4) Images
  doc.querySelectorAll("img").forEach((el) => {
    insertBlockText(el, "image");
  });

  // 5) Vidéo / audio éventuels
  doc.querySelectorAll("video").forEach((el) => {
    insertBlockText(el, "video");
  });

  doc.querySelectorAll("audio").forEach((el) => {
    insertBlockText(el, "audio");
  });

  // 6) Iframes restants : détecter la plateforme
  doc.querySelectorAll("iframe").forEach((el) => {
    const src = el.getAttribute("src") || "";

    if (/instagram\.com/i.test(src)) {
      insertBlockText(el, "Post : Instagram");
    } else if (/twitter\.com|platform\.twitter\.com|x\.com/i.test(src)) {
      insertBlockText(el, "Post : X");
    } else if (/facebook\.com/i.test(src)) {
      insertBlockText(el, "Post : Facebook");
    } else {
      insertBlockText(el, "Post : Iframe");
    }
  });

  // 7) Conserver les retours à la ligne utiles
  doc.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));

  doc.querySelectorAll("p, div, h1, h2, h3, h4, h5, h6, li").forEach((el) => {
    el.appendChild(doc.createTextNode("\n"));
  });

  let text = doc.body.textContent || "";

  // 8) Blindage final sur le texte complet
  text = text
    .replace(/\uFEFF/g, "")
    .replace(/\r/g, "")
    .replace(/Options d['’]affichage/gi, "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return text;
}

function getItemDate(item) {
  return item.orderDate || item.displayDate || item.creation || null;
}

function getTypeLabel(type) {
  const labels = {
    article: "ARTICLE",
    survey: "SONDAGE",
    quizz: "QUIZ",
    questionBox: "BOÎTE À QUESTIONS",
    notation: "NOTATION",
  };
  return labels[type] || (type || "ÉLÉMENT").toUpperCase();
}

function stringifyValue(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

/**
 * Construit le texte brut selon le type d'item
 */
function buildExportText(item) {
  const type = item.type;

  if (type === "article") {
    const titleText = htmlToCleanText(item.title || "");
    const contentText = htmlToCleanText(item.content || "");
    return [titleText, contentText].filter(Boolean).join("\n\n").trim();
  }

  if (type === "questionBox") {
    const lines = [];

    if (item.title) lines.push(`Titre : ${htmlToCleanText(item.title)}`);

    const questions = Array.isArray(item.questions) ? item.questions : [];
    if (questions.length) {
      lines.push("Questions :");
      questions.forEach((q, idx) => {
        const qDate = formatDate(q.datetime);
        const pseudo = q.pseudo ? ` (${q.pseudo})` : "";
        const question = htmlToCleanText(q.question || "");
        lines.push(`${idx + 1}. [${qDate}]${pseudo} ${question}`.trim());
      });
    }

    return lines.join("\n").trim();
  }

  if (type === "survey") {
  const lines = [];

  if (item.question) {
    lines.push(`Question : ${htmlToCleanText(item.question)}`);
  }

  if (item.nbVotes != null) {
    lines.push(`Votes totaux : ${item.nbVotes}`);
  }

  const choices = Array.isArray(item.choices) ? item.choices : [];

  if (choices.length) {
    lines.push("Choix :");

    choices.forEach((choice, choiceIndex) => {
      const choiceLabel = htmlToCleanText(choice.label || `Choix ${choiceIndex + 1}`);
      lines.push(`- ${choiceLabel}`);

      const entries = Array.isArray(choice.entries) ? choice.entries : [];
      entries.forEach((entry, entryIndex) => {
        const name = htmlToCleanText(entry.name || `Réponse ${entryIndex + 1}`);
        const percent = entry.percent != null ? ` — ${entry.percent}%` : "";
        const votes = entry.nbVotes != null ? ` (${entry.nbVotes} vote${entry.nbVotes > 1 ? "s" : ""})` : "";
        lines.push(`   • ${name}${percent}${votes}`);
      });
    });
  }

  return lines.join("\n").trim();
}

if (type === "quizz") {
  const lines = [];

  if (item.question) {
    lines.push(`Question : ${htmlToCleanText(item.question)}`);
  } else if (item.title) {
    lines.push(`Titre : ${htmlToCleanText(item.title)}`);
  }

  const choices = Array.isArray(item.choices) ? item.choices : [];
  if (choices.length) {
    lines.push("Réponses :");

    choices.forEach((choice, choiceIndex) => {
      const choiceLabel = htmlToCleanText(choice.label || `Choix ${choiceIndex + 1}`);

      // Certains quizz ont aussi entries comme les surveys
      const entries = Array.isArray(choice.entries) ? choice.entries : [];

      if (entries.length) {
        lines.push(`- ${choiceLabel}`);
        entries.forEach((entry, entryIndex) => {
          const name = htmlToCleanText(entry.name || `Réponse ${entryIndex + 1}`);
          const percent = entry.percent != null ? ` — ${entry.percent}%` : "";
          const votes = entry.nbVotes != null ? ` (${entry.nbVotes} vote${entry.nbVotes > 1 ? "s" : ""})` : "";
          const isCorrect = entry.isCorrect || entry.correct ? " ✅" : "";
          lines.push(`   • ${name}${percent}${votes}${isCorrect}`);
        });
      } else {
        // fallback simple
        lines.push(`- ${choiceLabel}`);
      }
    });
  }

  return lines.join("\n").trim();
}

  if (type === "notation") {
    const lines = [];
    if (item.title) lines.push(`Titre : ${htmlToCleanText(item.title)}`);
    if (item.content) lines.push(htmlToCleanText(item.content));

    // fallback générique si structure différente
    const usefulFields = ["playerName", "name", "comment", "description", "note", "value"];
    usefulFields.forEach((key) => {
      if (item[key] != null) {
        lines.push(`${key} : ${htmlToCleanText(stringifyValue(item[key]))}`);
      }
    });

    return [...new Set(lines)].join("\n").trim();
  }

  // fallback générique
  return htmlToCleanText(
    stringifyValue(item.title || "") + "\n" + stringifyValue(item.content || "")
  ).trim();
}

function sanitizeFilePart(value = "") {
  return String(value)
    .replace(/[^\w\-àâäéèêëïîôöùûüç ]/gi, "")
    .trim()
    .replace(/\s+/g, "_");
}

function formatNowForFilename() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const mi = String(now.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}_${hh}h${mi}`;
}

function getFrenchTypeSuffixes(types = []) {
  const frMap = {
    article: "articles",
    survey: "sondages",
    quizz: "quiz",
    questionBox: "boites_questions",
    notation: "notations",
  };

  if (types.includes("all")) return "complet";

  return types
    .map((t) => frMap[t] || t)
    .join("-");
}

/**
 * actualitesDetailsOrMatchData = soit:
 * - ancien format live.actualitesDetails
 * - nouveau format export full { articles, surveys, quizzs, questionBoxes, notations }
 */
export async function downloadLive(
  actualitesDetailsOrMatchData,
  liveName,
  selectedTypes = ["article"],
  options = {}
) {
  try {
    let source = actualitesDetailsOrMatchData;

    // Compat ancien format: [{ articles: [...] }]
    if (Array.isArray(actualitesDetailsOrMatchData)) {
      source = {
        articles: actualitesDetailsOrMatchData.flatMap((a) => a.articles || []),
        surveys: actualitesDetailsOrMatchData.flatMap((a) => a.surveys || []),
        quizzs: actualitesDetailsOrMatchData.flatMap((a) => a.quizzs || []),
        questionBoxes: actualitesDetailsOrMatchData.flatMap((a) => a.questionBoxes || []),
        notations: actualitesDetailsOrMatchData.flatMap((a) => a.notations || []),
      };
    }

    const mapByType = {
      article: source?.articles || [],
      survey: source?.surveys || [],
      quizz: source?.quizzs || [],
      questionBox: source?.questionBoxes || [],
      notation: source?.notations || [],
    };

    // "all" => tous les types
    const normalizedTypes = selectedTypes.includes("all")
      ? ["article", "survey", "quizz", "questionBox", "notation"]
      : selectedTypes;

    const mergedItems = normalizedTypes.flatMap((type) =>
      (mapByType[type] || []).map((item) => ({
        ...item,
        type: item.type || type,
      }))
    );

    if (!mergedItems.length) {
      alert("Aucun élément à exporter pour la sélection.");
      return;
    }

    // Tri chronologique global (récent -> ancien)
    const sortedItems = [...mergedItems].sort((a, b) => {
      const timeA = getItemDate(a) ? new Date(getItemDate(a)).getTime() : 0;
      const timeB = getItemDate(b) ? new Date(getItemDate(b)).getTime() : 0;
      return timeB - timeA;
    });

    const docChildren = [];

    const { userGroup = "" } = options;
    const exportDate = new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    }).format(new Date());

    docChildren.push(
    new Paragraph({
        children: [new TextRun({ text: `Export du live : ${liveName || "Live"}`, bold: true })],
        spacing: { after: 120 },
    })
    );

    docChildren.push(
    new Paragraph({
        children: [new TextRun({ text: `Date d’export : ${exportDate}` })],
    })
    );

    if (userGroup) {
    docChildren.push(
        new Paragraph({
        children: [new TextRun({ text: `Groupe : ${userGroup}` })],
        spacing: { after: 240 },
        })
    );
    } else {
    docChildren.push(new Paragraph({ text: "" }));
    }

    for (const item of sortedItems) {
      const fullText = buildExportText(item);

      // Ignore les items vides (ex: article image-only)
      if (!fullText) continue;

      const dateLine = formatDate(getItemDate(item));
      const typeLabel = getTypeLabel(item.type);

      // Ligne date + type
      docChildren.push(
        new Paragraph({
          children: [
            new TextRun({ text: `${dateLine} — ${typeLabel}`, bold: true }),
          ],
          spacing: { after: 120 },
        })
      );

      // Corps
      fullText.split("\n").forEach((line) => {
        docChildren.push(
          new Paragraph({
            children: [new TextRun(line)],
          })
        );
      });

      // Ligne vide entre items
      docChildren.push(new Paragraph({ text: "" }));
    }

    const doc = new Document({
      sections: [
        {
          children: docChildren.length
            ? docChildren
            : [new Paragraph("Aucun contenu texte exportable.")],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);

    const safeName = sanitizeFilePart(liveName || "live");
    const safeGroup = sanitizeFilePart(userGroup || "");
    const datePrefix = formatNowForFilename();
    const suffixFr = getFrenchTypeSuffixes(selectedTypes);

    const fileNameParts = [
    datePrefix,
    safeGroup && `groupe_${safeGroup}`,
    safeName,
    "export",
    suffixFr,
    ].filter(Boolean);

    saveAs(blob, `${fileNameParts.join("_")}.docx`);
  } catch (err) {
    console.error("Erreur export live:", err);
    alert("Erreur lors de l'export du live");
  }
}